import { useTranslation } from 'react-i18next';

import { AgGridServerTable } from 'core/components';
import { ActionButtonType } from 'core/enums';
import { useAgGridTableContext, useNotificationContext } from 'core/states';
import { groupByObjProp } from 'core/utils';
import { useCorporateCostingProfileActions } from 'corporate/hooks';
import { IPricingProfile } from 'pricing/types';
import { useQuoteActions } from 'quotes/hooks';
import { ICustomerQuote, IVendorQuote } from 'quotes/types';
import { Color, Variant } from 'theme/enums';

interface IProps {
  quote?: ICustomerQuote;
  pricingProfile?: IPricingProfile;
  onAttachVendorQuoteSuccess?: (attachedQuotes: IVendorQuote[]) => void;
}

export const VendorQuoteListTable = ({
  quote,
  pricingProfile,
  onAttachVendorQuoteSuccess,
}: IProps) => {
  const { t } = useTranslation();
  const { selectedRows } = useAgGridTableContext();
  const { clearNotificationTimeout } = useNotificationContext();

  const { attachQuoteToQuote } = useQuoteActions();

  const { attachVendorQuotesToCostingProfile } = useCorporateCostingProfileActions({
    selectionCount: selectedRows?.length,
  });

  const selectionActions = () => [
    {
      type: ActionButtonType.BUTTON,
      label: t('buttonLabelAttachSelectedVendorQuotes', {
        count: selectedRows?.length,
      }),
      color: Color.SUCCESS,
      variant: Variant.SOLID,
      onClick: () => attachVendorQuotes(),
    },
  ];

  const attachVendorQuotes = async () => {
    if (pricingProfile) {
      const { success } = await attachVendorQuotesToCostingProfile({
        pricingProfileId: pricingProfile.id,
        pricingProfileQuoteAssociations: selectedRows.map((vendorQuote) => ({
          discountStructureId: pricingProfile.vendorCostStructureId,
          quoteId: vendorQuote.id,
        })),
      });

      if (success) {
        clearNotificationTimeout();
        onAttachVendorQuoteSuccess?.(selectedRows);

        return true;
      }

      return false;
    }

    const { success, quoteQuoteAssociations } = await attachQuoteToQuote({
      quoteQuoteAssociations: selectedRows?.map((selectedQuote) => ({
        quoteId: quote?.id,
        attachedQuoteId: selectedQuote.id,
      })),
    });

    if (success) {
      const groupedQuoteQuoteAssociations = groupByObjProp(
        quoteQuoteAssociations,
        'attachedQuoteId'
      );

      clearNotificationTimeout();
      onAttachVendorQuoteSuccess?.(
        selectedRows?.map((selectedRow) => ({
          ...selectedRow,
          quoteQuoteAssociationId: groupedQuoteQuoteAssociations?.[selectedRow?.id]?.id,
        }))
      );

      return true;
    }

    return false;
  };

  return (
    <>
      <AgGridServerTable selectionActions={selectionActions} />
    </>
  );
};
